import { motion } from 'framer-motion';
import { COLORS, EASING } from '../constants/theme';

const steps = [
  { n: '01', title: 'Discovery', desc: 'We map your workflows and identify the highest-impact AI opportunities.' },
  { n: '02', title: 'Build',     desc: 'Our engineers design and integrate custom models and automation pipelines.' },
  { n: '03', title: 'Deploy',    desc: 'Secure, monitored rollout with governance, SLAs, and continuous optimization.' },
];

export default function About() {
  return (
    <section id="about" style={{ padding: '96px 0', background: COLORS.white }}>
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'start' }} className="about-grid">

          <motion.div
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: EASING.spring }}
          >
            <div className="section-eyebrow">About Us</div>
            <h2 className="section-title" style={{ marginBottom: 20 }}>
              Building Reliable AI Products for Real{' '}
              <span className="gradient-text">Business Workflows.</span>
            </h2>
            <p style={{ fontSize: 16, color: COLORS.text2, lineHeight: 1.75, marginBottom: 16 }}>
              Reano AI Technologies partners with organizations to design, build, and deploy AI systems that improve speed, accuracy, and customer experience.
            </p>
            <p style={{ fontSize: 16, color: COLORS.text2, lineHeight: 1.75, marginBottom: 40 }}>
              From discovery and data strategy to deployment and optimization, we deliver measurable outcomes with engineering-first execution.
            </p>
            <a href="#contact" className="btn-primary">
              Learn About Reano
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2.5 7.5h10M8.5 3.5l4 4-4 4"/>
              </svg>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: EASING.spring, delay: 0.1 }}
          >
            <div style={{ marginBottom: 28 }}>
              <div className="section-eyebrow">How We Work</div>
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute', left: 19, top: 24, bottom: 24,
                width: 2, background: `linear-gradient(180deg, ${COLORS.blue} 0%, #C8E9F5 100%)`,
                borderRadius: 2,
              }}/>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {steps.map((s, i) => (
                  <motion.div
                    key={s.n}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.12, ease: EASING.spring }}
                    style={{ display: 'flex', gap: 20, paddingBottom: i < steps.length - 1 ? 32 : 0 }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: COLORS.blue, color: COLORS.white,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, letterSpacing: '.02em',
                      position: 'relative', zIndex: 1,
                      boxShadow: `0 0 0 4px ${COLORS.white}, 0 0 0 5px rgba(71,158,195,0.25)`,
                    }}>{s.n}</div>
                    <div style={{ paddingTop: 8 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>{s.title}</div>
                      <div style={{ fontSize: 14.5, color: COLORS.text2, lineHeight: 1.6 }}>{s.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.55 }}
              style={{
                marginTop: 32,
                background: `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.blueDark})`,
                borderRadius: 14, padding: '22px 24px',
                display: 'flex', alignItems: 'center', gap: 14,
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.8">
                  <path d="M10 2l2.5 5 5.5.8-4 3.9.94 5.5L10 14.5l-4.94 2.7.94-5.5-4-3.9 5.5-.8L10 2z"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.white, marginBottom: 2 }}>Built for Impact</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.82)' }}>Fast, measurable outcomes every engagement</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
        }
      `}</style>
    </section>
  );
}
